import React from "react";
import { ExternalLink } from "lucide-react";

/**
 * AhrefsBreakdowns — dedicated SEO widget that renders the four
 * paid Site Explorer breakdowns returned by `ahrefs_integration`:
 *
 *   • top_organic_keywords   → position/volume/traffic table (the
 *     headline "what queries drive traffic" evidence).
 *   • top_pages              → the URLs pulling that traffic in.
 *   • top_referring_domains  → who's linking (DR + link count).
 *   • top_backlinks          → the actual highest-authority links.
 *
 * Renders compact tables (max 10 rows each per backend sampling) so
 * a reader absorbs the SEO story at a glance without opening AHREFS.
 * Sections auto-hide when the corresponding breakdown is empty — a
 * target with 0 organic keywords should not show an empty table.
 *
 * Used from both the report view (`ViewReportPage`) and the per-
 * property live dashboard on the Feeds page (`ClientAhrefsFeedsPage`).
 */
export function AhrefsBreakdowns({ breakdowns, testIdPrefix }) {
  const b = breakdowns || {};
  const kws = b.top_organic_keywords || [];
  const pages = b.top_pages || [];
  const refdoms = b.top_referring_domains || [];
  const bls = b.top_backlinks || [];
  if (!kws.length && !pages.length && !refdoms.length && !bls.length) {
    return null;
  }
  return (
    <div className="mt-3 space-y-3"
      data-testid={`${testIdPrefix}-ahrefs-breakdowns`}>
      {!!kws.length && (
        <AhrefsTable
          title="Top organic keywords"
          subtitle="Sampled by traffic — position, monthly search volume, and estimated organic traffic."
          testId={`${testIdPrefix}-ahrefs-keywords`}
          columns={[
            { key: "keyword", label: "Keyword", grow: true, mono: false },
            { key: "position", label: "Pos.", digits: 0, numeric: true, width: "3.5rem" },
            { key: "volume", label: "Volume", numeric: true, width: "5rem" },
            { key: "traffic", label: "Traffic", numeric: true, width: "5rem" },
            { key: "traffic_value", label: "Value", numeric: true,
              prefix: "$", width: "5rem" },
          ]}
          rows={kws}
        />
      )}
      {!!pages.length && (
        <AhrefsTable
          title="Top pages"
          subtitle="Pages ranked by estimated organic traffic in the reporting period."
          testId={`${testIdPrefix}-ahrefs-pages`}
          columns={[
            { key: "url", label: "URL", grow: true, mono: true, truncate: true },
            { key: "traffic", label: "Traffic", numeric: true, width: "5rem" },
            { key: "traffic_value", label: "Value", numeric: true,
              prefix: "$", width: "5rem" },
            { key: "keywords", label: "Kw", numeric: true, width: "3.5rem" },
          ]}
          rows={pages}
        />
      )}
      {!!refdoms.length && (
        <AhrefsTable
          title="Top referring domains"
          subtitle="Sampled by Domain Rating — the strongest linking sites."
          testId={`${testIdPrefix}-ahrefs-refdomains`}
          columns={[
            { key: "domain", label: "Domain", grow: true, mono: true, truncate: true },
            { key: "domain_rating", label: "DR", numeric: true, digits: 0, width: "3.5rem" },
            { key: "backlinks", label: "Links", numeric: true, width: "4rem" },
            { key: "first_seen", label: "First seen", width: "6rem",
              formatter: _formatShortDate },
          ]}
          rows={refdoms}
        />
      )}
      {!!bls.length && (
        <AhrefsTable
          title="Top backlinks"
          subtitle="Highest-DR referring pages currently linking to this target."
          testId={`${testIdPrefix}-ahrefs-backlinks`}
          columns={[
            { key: "source_url", label: "Referring page",
              grow: true, mono: true, truncate: true, link: true },
            { key: "anchor", label: "Anchor", width: "10rem", truncate: true },
            { key: "domain_rating_source", label: "DR",
              numeric: true, digits: 0, width: "3.5rem" },
            { key: "link_type", label: "Type", width: "4.5rem",
              formatter: _formatBacklinkType },
          ]}
          rows={bls}
        />
      )}
    </div>
  );
}

function _formatShortDate(v) {
  return v ? String(v).slice(0, 10) : "—";
}

function _formatBacklinkType(v) {
  const isDofollow = String(v || "").toLowerCase() === "dofollow";
  return (
    <span className={`px-1 py-0.5 rounded-sm text-[9px] font-mono uppercase
      ${isDofollow ? "bg-emerald-100 text-emerald-800"
                   : "bg-zinc-100 text-zinc-600"}`}>
      {v || "—"}
    </span>
  );
}

function AhrefsTable({ title, subtitle, columns, rows, testId }) {
  return (
    <div className="rounded-sm border border-zinc-200 bg-white overflow-hidden"
      data-testid={testId}>
      <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50">
        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-800">
          {title}
        </div>
        {subtitle && (
          <div className="text-[10px] text-zinc-500 mt-0.5 leading-snug">
            {subtitle}
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50/60">
              {columns.map((c) => (
                <th key={c.key}
                  className={`px-2 py-1.5 font-mono uppercase text-[9px]
                    tracking-wider text-zinc-500 ${c.numeric ? "text-right" : "text-left"}`}
                  style={c.width ? { width: c.width } : undefined}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${String(r[columns[0]?.key] ?? "row")}-${i}`}
                className="border-b border-zinc-50 last:border-b-0">
                {columns.map((c) => {
                  const raw = r[c.key];
                  let content;
                  if (c.formatter) {
                    content = c.formatter(raw, r);
                  } else if (c.numeric) {
                    if (raw === null || raw === undefined || raw === "") {
                      content = "—";
                    } else {
                      const n = Number(raw);
                      if (Number.isNaN(n)) {
                        content = String(raw);
                      } else {
                        const digits = c.digits ?? (Math.abs(n) < 10 && n !== 0 ? 2 : 0);
                        content = `${c.prefix || ""}${n.toLocaleString(undefined,
                          { minimumFractionDigits: digits,
                            maximumFractionDigits: digits })}${c.suffix || ""}`;
                      }
                    }
                  } else if (raw === null || raw === undefined || raw === "") {
                    content = "—";
                  } else if (c.link && typeof raw === "string" &&
                      /^https?:\/\//i.test(raw)) {
                    content = (
                      <a href={raw} target="_blank" rel="noreferrer"
                        className="text-blue-600 hover:underline
                          inline-flex items-center gap-1">
                        <span className={c.truncate ? "truncate max-w-[280px] inline-block align-bottom" : ""}>
                          {raw}
                        </span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    );
                  } else {
                    content = c.truncate ? (
                      <span className="truncate max-w-[320px] inline-block align-bottom"
                        title={String(raw)}>{String(raw)}</span>
                    ) : String(raw);
                  }
                  return (
                    <td key={c.key}
                      className={`px-2 py-1.5 align-top
                        ${c.numeric ? "text-right font-mono" : ""}
                        ${c.mono && !c.numeric ? "font-mono text-[10px]" : ""}`}
                      style={c.width ? { width: c.width } : undefined}>
                      {content}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
